import React, { FunctionComponent, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useCookies } from "react-cookie";

import MenuSelector from "./MenuSelector";
import LegacyMenuSelector from "./LegacyMenuSelector";
import { HeaderContainer, LogoWrapper, StyledHeader, OverlayContainer, FixedHeaderOffset } from "./styles";

import BackButton from "components/BackButton";
import VodafoneLogo from "components/IconImage/vodafone";
import LowiLogo from "components/IconImage/lowi";
import { userState } from "state/user-slice";
import { configState } from "state/config-slice";
import { isFeatureActive } from "utils/getFeature";
import { ErrorCode } from "types/CustomError";

const headerConfigs = {
  isBackButtonVisible: false,
  customBackButtonPath: null,
  isLogoVisible: true,
  isLogButtonsVisible: true,
  isCartVisible: false,
  centerLogo: false,
  isHeaderVisible: true,
};

const getHeaderConfigurations = (route: string, isUserLoggedIn: boolean) => {
  const routeSteps = route.split("/");

  switch (routeSteps[1]) {
    case "not-eligible-user":
      return {
        ...headerConfigs,
        isHeaderVisible: false,
      };
    case "offer":
      if (routeSteps.length === 4 && routeSteps[3] === "cancellation") {
        return {
          ...headerConfigs,
          isBackButtonVisible: true,
          isLogoVisible: true,
          isLogButtonsVisible: false,
          isCartVisible: false,
          centerLogo: true,
        };
      }

      if (routeSteps.length === 4 && routeSteps[3] === "pre-setup-form") {
        return {
          ...headerConfigs,
          isBackButtonVisible: true,
          isLogButtonsVisible: false,
        };
      }

      if (routeSteps.length === 4 && routeSteps[3] === "not-eligible") {
        return {
          ...headerConfigs,
          isLogoVisible: false,
          isBackButtonVisible: true,
          isLogButtonsVisible: false,
        };
      }

      return {
        ...headerConfigs,
        isBackButtonVisible: true,
        isLogoVisible: false,
        isCartVisible: isUserLoggedIn,
      };

    case "multiplanoffer":
      return {
        ...headerConfigs,
        isBackButtonVisible: true,
        isLogoVisible: false,
        isCartVisible: isUserLoggedIn,
      };

    case "more-info":
    case "select-credit-card":
    case "checkout":
    case "basket":
      return {
        ...headerConfigs,
        isBackButtonVisible: true,
        isLogButtonsVisible: false,
        centerLogo: true,
      };
    case "cable":
      return {
        ...headerConfigs,
        isBackButtonVisible: false,
        isLogButtonsVisible: false,
        isCartVisible: false,
        centerLogo: true,
      };
    case "myaccount":
      return {
        ...headerConfigs,
        isLogoVisible: false,
        isBackButtonVisible: true,
      };
    case "":
    default:
      return {
        ...headerConfigs,
        isCartVisible: isUserLoggedIn,
      };
  }
};

const Header: FunctionComponent = () => {
  const userInfo = useSelector(userState);
  const { pathname } = useLocation();
  const config = useSelector(configState);
  const [cookies, ,] = useCookies();
  const isUserValid = useMemo(() => {
    return cookies.ERRORCODE !== ErrorCode.USERNOTVALID;
  }, [cookies.ERRORCODE]);

  const {
    isHeaderVisible,
    isBackButtonVisible,
    isLogoVisible,
    isLogButtonsVisible,
    isCartVisible,
    customBackButtonPath,
    centerLogo,
  } = useMemo(() => getHeaderConfigurations(pathname, userInfo.loggedIn), [pathname, userInfo.loggedIn]);

  return (
    <>
      {isHeaderVisible && (
        <OverlayContainer visible={config.isMenuOpen}>
          <FixedHeaderOffset />
          <StyledHeader>
            <HeaderContainer isLegacyHeader={isFeatureActive("PPE 1.0 Backend")}>
              {isBackButtonVisible && <BackButton customBackButtonPath={customBackButtonPath} />}
              {isLogoVisible && isUserValid && (
                <LogoWrapper
                  to="/"
                  aria-label="Go to homepage"
                  $ofsetLogo={isBackButtonVisible && isLogoVisible}
                  $centerLogo={centerLogo}
                >
                  {isFeatureActive("Enabled default theme") || isFeatureActive("Enabled CZ theme") ? (
                    <VodafoneLogo />
                  ) : (
                    <LowiLogo />
                  )}
                </LogoWrapper>
              )}

              {isFeatureActive("PPE 2.0 Backend") ? (
                isUserValid && (
                  <MenuSelector
                    isCartVisible={isCartVisible}
                    isLogButtonsVisible={isLogButtonsVisible}
                    isLogoCentered={centerLogo}
                  />
                )
              ) : (
                <LegacyMenuSelector isCartVisible={isCartVisible} isLogButtonsVisible={isLogButtonsVisible} />
              )}
            </HeaderContainer>
          </StyledHeader>
        </OverlayContainer>
      )}
    </>
  );
};

export default React.memo(Header);
